/**
 * Recursive `list_types` branch loading and branch path ids (`ListTypesBranch.id`).
 * Full grammar (hops, palette field/group ids, normalization): `docs/passport-builder-v2-palette-ids.md` at repository root.
 */
import type { NestedColumnSegment } from '@/features/Inventory/ObjectTypePassportGrid/model/types'
import type { ObjectInventoryField } from '@/entities/Inventory/ObjectTypes'
import type { ObjectTypeGroup } from '@/entities/Inventory/ObjectSubTypes'
import {
  fieldTypesFromClasses,
  normalizeObjectTypeFieldsResponse,
  objectTypeFieldsToInventoryFields,
} from './objectTypeFields'

const MAX_DEPTH = 3

/** Between hops along the `list_types` chain (not inside a hop triple). */
export const LIST_TYPES_PATH_LEVEL_SEP = '->'

type TypeFieldResponse = {
  fields?: ObjectInventoryField[]
  name?: string
  object_name?: string
  type_name?: string
  object_type_name?: string
  invent_object_type?: { name?: string; object_name?: string }
}

function pickStr(...vals: unknown[]): string {
  for (const v of vals) {
    if (typeof v === 'string' && v.trim() !== '') return v.trim()
  }
  return ''
}

function unwrapTypeFieldBody(raw: unknown): Record<string, unknown> | null {
  if (raw == null || typeof raw !== 'object') return null
  const r = raw as Record<string, unknown>
  const inner = r.data
  if (inner != null && typeof inner === 'object') return inner as Record<string, unknown>
  return r
}

function catalogLabelFromTypeFieldBody(body: Record<string, unknown> | null): string {
  if (!body) return ''
  const iot = body.invent_object_type
  if (iot && typeof iot === 'object') {
    const o = iot as Record<string, unknown>
    const fromIot = pickStr(o.name, o.object_name)
    if (fromIot) return fromIot
  }
  return pickStr(body.name, body.object_name, body.type_name, body.object_type_name)
}

type HttpGet = {
  get: (url: string) => Promise<{ data: unknown } & Record<string, unknown>>
}

type ObjectTypeDetail = {
  fields: ObjectInventoryField[]
  label: string
}

function parseObjectTypeDetailFromFieldsResponse(res: unknown): ObjectTypeDetail {
  const normalized = normalizeObjectTypeFieldsResponse(res)
  const fieldTypes = fieldTypesFromClasses(normalized.fieldClasses)
  const body = unwrapTypeFieldBody((res as { data?: unknown })?.data ?? res)
  const fields = objectTypeFieldsToInventoryFields(normalized.fields, fieldTypes)
  const label = catalogLabelFromTypeFieldBody(body)
  return { fields, label }
}

/**
 * Загружает поля целевого типа из новой нормализованной модели.
 */
async function fetchObjectTypeDetail(
  http: HttpGet,
  groupId: string,
  objectTypeId: number,
): Promise<ObjectTypeDetail> {
  try {
    const res = await http.get(`/object-types/${objectTypeId}/fields`)
    return parseObjectTypeDetailFromFieldsResponse(res)
  } catch {
    try {
      const res = await http.get(`/inventory/group/${groupId}/type-field/${objectTypeId}`)
      const rawBody = res.data as TypeFieldResponse | undefined
      const body = unwrapTypeFieldBody(res.data)
      return {
        fields: rawBody?.fields ?? (body?.fields as ObjectInventoryField[] | undefined) ?? [],
        label: catalogLabelFromTypeFieldBody(body),
      }
    } catch {
      return { fields: [], label: '' }
    }
  }
}

async function fetchCatalogLabelFromObjectTypeRow(
  http: HttpGet,
  groupId: string,
  linkedTypeId: number,
  isCancelled?: () => boolean,
): Promise<string> {
  if (!Number.isFinite(linkedTypeId) || linkedTypeId <= 0) return ''
  const detail = await fetchObjectTypeDetailCached(http, groupId, linkedTypeId)
  if (isCancelled?.()) return ''
  return detail.label
}

export type ListTypesBranch = {
  id: string
  title: string
  parentPath: string
  /** Inventory type whose fields are in `fields`. */
  objectTypeId: number
  /** Hops from the root child type to this branch (empty at root). */
  segments: NestedColumnSegment[]
  fields: ObjectInventoryField[]
  isLoading: boolean
  isError: boolean
  /** True when this branch has further `list_types` hops that were not loaded (max depth). */
  nestedDepthLimitReached: boolean
  /** Human label for the linked inventory type, shown under the field title in the palette. */
  linkedCatalogLabel: string
}

const objectTypeDetailCache = new Map<string, Promise<ObjectTypeDetail>>()

/**
 * Cached type detail from normalized fields endpoint.
 * The same type is never fetched twice across branches or re-renders within the session.
 */
export function fetchObjectTypeDetailCached(
  http: HttpGet,
  groupId: string,
  objectTypeId: number,
): Promise<ObjectTypeDetail> {
  const key = `${groupId}:${objectTypeId}`
  if (objectTypeDetailCache.has(key)) return objectTypeDetailCache.get(key)!

  const p = fetchObjectTypeDetail(http, groupId, objectTypeId)
  objectTypeDetailCache.set(key, p)
  return p
}

/**
 * One hop: on type `sourceOt`, the `list_types` field `edgeFieldId` points to linked type `targetOt`.
 * Field display names are not part of the id (see branch `title` / palette `displayName`).
 */
export function formatListTypesHop(
  sourceOt: number,
  edgeFieldId: string | number,
  targetOt: number,
): string {
  return `[${sourceOt}:${edgeFieldId}:${targetOt}]`
}

export function appendListTypesHopPath(pathSoFar: string, hop: string): string {
  return `${pathSoFar}${LIST_TYPES_PATH_LEVEL_SEP}${hop}`
}

/**
 * Inventory types that must not be loaded as **nested** `list_types` hops when building the palette.
 * Includes the edited catalog id plus every LIST group’s {@code list_invent_object_type_id} on this
 * passport (nested targets that match those LIST-linked types or the catalog id are skipped).
 */
export function computeNestedSkipBranchTypeIds(
  editedCatalogTypeId: number,
  fieldGroups: ObjectTypeGroup[],
): Set<number> {
  const s = new Set<number>()
  if (editedCatalogTypeId > 0) s.add(editedCatalogTypeId)

  for (const g of fieldGroups) {
    if (String(g.type ?? '').toUpperCase() !== 'LIST') continue
    const lid = Number(g.list_invent_object_type_id)
    if (Number.isFinite(lid) && lid > 0) s.add(lid)
  }
  return s
}

export async function fetchAutoRelationBranchesForRoot(
  http: HttpGet,
  groupId: string,
  targetObjectTypeId: number,
  rootTitle: string,
  isCancelled?: () => boolean,
): Promise<ListTypesBranch[]> {
  const acc: ListTypesBranch = {
    id: `auto-${targetObjectTypeId}`,
    title: rootTitle,
    parentPath: '',
    objectTypeId: targetObjectTypeId,
    segments: [],
    fields: [],
    isLoading: true,
    isError: false,
    nestedDepthLimitReached: false,
    linkedCatalogLabel: '',
  }

  try {
    const detail = await fetchObjectTypeDetailCached(http, groupId, targetObjectTypeId)
    if (isCancelled?.()) return []
    acc.fields = detail.fields
    acc.linkedCatalogLabel = detail.label
  } catch {
    if (isCancelled?.()) return []
    acc.isError = true
  } finally {
    if (!isCancelled?.()) {
      acc.isLoading = false
    }
  }

  return [acc]
}
export function isNestedFeedingPointListTypesFieldName(fieldName: string | undefined): boolean {
  const n = String(fieldName ?? '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ')
  return /^пункт(ы)?\s+питания\b/.test(n)
}

/** LIST `ObjectTypeGroup.name` is the feeding-point label — omit whole row from «Перечни» palette. */
export function isFeedingPointListGroupLabel(name: string | undefined): boolean {
  const n = String(name ?? '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ')
  return /^пункт(ы)?\s+питания\b/.test(n)
}

/**
 * Branch/group title as shown in the sidebar (e.g. «ПУНКТ ПИТАНИЯ. БАЛАНС») — omit that branch row.
 * Matches a path segment «пункт … питания», not unrelated text.
 */
export function isFeedingPointDuplicatePaletteBranchTitle(displayTitle: string | undefined): boolean {
  const t = String(displayTitle ?? '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ')
  if (/^пункт(ы)?\s+питания\b/.test(t)) return true
  return /(^|[.\s])пункт(ы)?\s+питания([\s.]|$)/.test(t)
}

/** Drop LIST palette fetch/row when it duplicates the configured catalog or is a feeding-point group. */
export function shouldOmitListPaletteGroup(g: ObjectTypeGroup, editedCatalogTypeId: number): boolean {
  if (String(g.type ?? '').toUpperCase() !== 'LIST') return false
  const lid = Number(g.list_invent_object_type_id)
  if (editedCatalogTypeId > 0 && Number.isFinite(lid) && lid === editedCatalogTypeId) return true
  return isFeedingPointListGroupLabel(g.name)
}

/**
 * Fetches sidebar branches for each `list_types` hop via normalized fields API.
 *
 * @param editedObjectTypeId — catalog object type id of the passport being edited (source of the root hop).
 * @param rootObjectTypeId — linked type to load for the root branch.
 * @param rootEdgeFieldId — id of the root `list_types` field on the edited type (references parent).
 *
 * **Root self-loop:** Root branch is skipped when {@code rootObjectTypeId} equals {@code editedObjectTypeId}.
 *
 * **Nested skip:** Deeper hops are skipped when {@code objectTypeId} is in {@code nestedSkipBranchTypeIds}
 * (see {@link computeNestedSkipBranchTypeIds}) so LIST-linked types (e.g. «Пункт питания») are not expanded
 * again under another branch when their id differs from the route catalog id.
 */
export async function fetchListTypesBranchesForRoot(
  http: HttpGet,
  groupId: string,
  editedObjectTypeId: number,
  rootObjectTypeId: number,
  rootEdgeFieldId: string | number,
  rootTitle: string,
  rootParentPath: string,
  isCancelled?: () => boolean,
  nestedSkipBranchTypeIds?: Set<number>,
): Promise<ListTypesBranch[]> {
  const nestedSkip = nestedSkipBranchTypeIds ?? new Set<number>()
  const accs: ListTypesBranch[] = []

  async function fetchBranch(
    objectTypeId: number,
    title: string,
    parentPath: string,
    depth: number,
    id: string,
    segments: NestedColumnSegment[],
    isRootBranch: boolean,
  ): Promise<void> {
    if (isCancelled?.()) return

    if (isRootBranch) {
      if (editedObjectTypeId > 0 && objectTypeId === editedObjectTypeId) {
        return
      }
    } else if (nestedSkip.has(objectTypeId)) {
      return
    }

    const acc: ListTypesBranch = {
      id,
      title,
      parentPath,
      objectTypeId,
      segments,
      fields: [],
      isLoading: true,
      isError: false,
      nestedDepthLimitReached: false,
      linkedCatalogLabel: '',
    }
    accs.push(acc)

    let fields: ObjectInventoryField[] = []
    try {
      if (isCancelled?.()) return
      const detail = await fetchObjectTypeDetailCached(http, groupId, objectTypeId)
      if (isCancelled?.()) return
      fields = detail.fields
      acc.fields = fields
      acc.linkedCatalogLabel = detail.label
    } catch {
      if (isCancelled?.()) return
      acc.isError = true
    } finally {
      if (!isCancelled?.()) {
        acc.isLoading = false
      }
    }

    if (isCancelled?.()) return

    if (!acc.isError && !acc.linkedCatalogLabel && objectTypeId > 0) {
      acc.linkedCatalogLabel = await fetchCatalogLabelFromObjectTypeRow(
        http,
        groupId,
        objectTypeId,
        isCancelled,
      )
    }

    const nested = fields.filter((f) => f.type === 'list_types' && Number(f.object_type) > 0)
    for (const nf of nested) {
      if (depth + 1 > MAX_DEPTH) {
        acc.nestedDepthLimitReached = true
        break
      }
      if (isNestedFeedingPointListTypesFieldName(nf.name)) {
        continue
      }
      const nextTitle = `${title}. ${nf.name}`
      const nextPath = `[${nextTitle}]`
      const hop = formatListTypesHop(objectTypeId, nf.id, Number(nf.object_type))
      const childId = appendListTypesHopPath(id, hop)
      const nextSegments: NestedColumnSegment[] = [
        ...segments,
        { object_type_id: objectTypeId, field_id: Number(nf.id) },
      ]
      await fetchBranch(
        Number(nf.object_type),
        nextTitle,
        nextPath,
        depth + 1,
        childId,
        nextSegments,
        false,
      )
      if (isCancelled?.()) return
    }
  }

  const rootId = formatListTypesHop(editedObjectTypeId, rootEdgeFieldId, rootObjectTypeId)
  await fetchBranch(rootObjectTypeId, rootTitle, rootParentPath, 1, rootId, [], true)
  return accs
}
